/* /lifecycle — onboarding checklists, probation reviews, offboarding (clearance, acknowledgement, settlement) and new offboarding. */
import "./pay.css";
import { useState } from "react";
import { useAcknowledgeResignation, useCreateOffboarding, useLoans, useOffboarding, useOnboarding, useSettlement, useToday, useToggleClearance, useToggleOnboardingTask } from "../../data/hooks";
import { addDays, daysTo } from "../../domain/dates";
import { settlement, wageOf } from "../../domain/payroll";
import type { Employee, Offboarding } from "../../data/types";
import { useOverlays } from "../../app/overlays";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import {
  Alert, Bar, Button, EmptyState, ErrorBox, Icon, Kv, Modal, Money, PageHeader, Person, SelectField, Skeleton, Tabs, TextField, cx, errMsg, useToast,
} from "../../ui";
import { StatusChip, unknownEmp, useEmpMap } from "./components/common";
import { activeCode } from "../../lib/country";

export const ONB_TASKS: [string, string][] = [["Offer letter signed", "توقيع خطاب العرض"], ["Qiwa contract authenticated", "توثيق العقد في قوى"], ["GOSI registration", "التسجيل في التأمينات"], ["Medical insurance (CCHI) added", "إضافة التأمين الطبي"], ["Iqama / visa issued", "إصدار الإقامة / التأشيرة"], ["Bank IBAN for Mudad", "آيبان البنك لمدد"], ["Laptop, SIM & access card", "الحاسب والشريحة وبطاقة الدخول"], ["Policies acknowledged", "الإقرار بالسياسات"]];
export const CLR_TASKS: [string, string][] = [["Resignation / termination letter on file", "خطاب الاستقالة / الإنهاء"], ["Handover completed", "تسليم المهام"], ["Assets returned", "إعادة العهد"], ["Final settlement paid (within 7 days)", "صرف المخالصة (خلال 7 أيام)"], ["GOSI & Qiwa contract ended", "إنهاء التأمينات وعقد قوى"], ["Service certificate issued (Art. 64)", "إصدار شهادة الخدمة (م 64)"]];
export const OFFB_TYPES: [string, string, string, number][] = [
  ["Resignation", "Resignation by employee", "استقالة الموظف", 30],
  ["Termination", "Termination by employer · Art. 77", "إنهاء من صاحب العمل · م 77", 60],
  ["Contract end", "Fixed-term contract ended", "انتهاء العقد محدد المدة", 0],
  ["Probation not confirmed", "Probation not confirmed · Art. 54", "عدم التثبيت بعد التجربة · م 54", 0],
  ["Retirement", "Retirement", "التقاعد", 0],
];
const typeLabel = (k: string, isAr: boolean) => { const x = OFFB_TYPES.find((o) => o[0] === k); return x ? (isAr ? x[2] : x[1]) : k; };

function OnbModal({ employeeId, onClose }: { employeeId: number; onClose: () => void }) {
  const { L, name, t } = useI18n();
  const toast = useToast();
  const q = useOnboarding();
  const emps = useEmpMap();
  const toggle = useToggleOnboardingTask();
  const o = q.data?.find((x) => x.employeeId === employeeId);
  const e = emps.map.get(employeeId);
  return (
    <Modal title={L("Onboarding", "المباشرة") + " · " + (e ? name(e) : "#" + employeeId)} onClose={onClose} testId="onb-modal" foot={<Button onClick={onClose}>{t("Close")}</Button>}>
      {!o ? <Skeleton /> : (
        <div className="stack">
          <Bar value={o.tasks.filter(Boolean).length} max={ONB_TASKS.length} />
          {ONB_TASKS.map(([en, ar], i) => (
            <label key={en} className="check">
              <input type="checkbox" checked={!!o.tasks[i]} disabled={toggle.isPending} data-testid={"onb-task-" + i}
                onChange={() => toggle.mutate({ employeeId, index: i }, { onError: (err) => toast(errMsg(err), "bad") })} /> {L(en, ar)}
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

function OffbModal({ id, onClose }: { id: number; onClose: () => void }) {
  const { L, name, lang, t, isAr } = useI18n();
  const toast = useToast();
  const today = useToday();
  const { openPrint } = useOverlays();
  const q = useOffboarding();
  const emps = useEmpMap();
  const sq = useSettlement(id);
  const toggle = useToggleClearance();
  const ack = useAcknowledgeResignation();
  const o = q.data?.find((x) => x.id === id);
  const e = o ? emps.map.get(o.employeeId) : undefined;
  if (!o) return <Modal title={L("Offboarding", "إنهاء الخدمة")} onClose={onClose} testId="offb-modal"><Skeleton /></Modal>;
  const settled = o.status === "Settled";
  const st = sq.data;
  const wdBy = addDays(o.submitted, 7), ackBy = addDays(o.submitted, 30);
  const factor = st ? (st.eo.factor === 0 ? "0" : st.eo.factor === 1 ? L("full", "كامل") : st.eo.factor < 0.5 ? "⅓" : "⅔") : "";
  const noEosb = o.type === "Probation not confirmed";
  return (
    <Modal className="wide" title={(e ? name(e) : "#" + o.employeeId) + " · " + typeLabel(o.type, isAr)} onClose={onClose} testId="offb-modal"
      foot={<>
        <Button icon="file" onClick={() => openPrint({ doc: "letter", emp: o.employeeId, letter: "Experience letter" })} data-testid="btn-service-cert">{L("Service certificate", "شهادة الخدمة")}</Button>
        {o.type === "Resignation" && !o.acknowledged && <Button icon="check" onClick={() => ack.mutate(o.id, { onSuccess: () => toast(L("Resignation acknowledged", "تم استلام الاستقالة")), onError: (err) => toast(errMsg(err), "bad") })} loading={ack.isPending} data-testid="btn-ack">{L("Acknowledge resignation", "استلام الاستقالة")}</Button>}
        <Button variant="pri" icon="wallet" onClick={() => toast(L("Off-cycle final settlement created for " + (e?.nameEn ?? ""), "تم إنشاء مسير المخالصة لـ " + (e?.nameAr ?? "")))} data-testid="btn-run-settlement">{L("Run final settlement", "تشغيل المخالصة")}</Button>
      </>}>
      <div className="g g-2">
        <div className="stack">
          {o.type === "Resignation" ? (
            <ol className="steps" data-testid="resign-steps">
              {[
                [L("Resignation submitted in writing", "تقديم الاستقالة كتابيًا"), gDate(o.submitted, lang), "done"],
                [L("Withdrawal window (7 days)", "مهلة السحب (7 أيام)"), L("until ", "حتى ") + gDate(wdBy, lang), daysTo(today, wdBy) < 0 ? "done" : "cur"],
                [o.acknowledged ? L("Acknowledged by employer", "استلمها صاحب العمل") : L("Deemed accepted unless deferred", "تعد مقبولة ما لم تؤجل"), gDate(ackBy, lang) + L(" · defer up to 60 days with written reasons", " · يمكن التأجيل حتى 60 يومًا كتابيًا"), o.acknowledged || daysTo(today, ackBy) < 0 ? "done" : "cur"],
                [L("Notice ends · last working day", "انتهاء الإشعار · آخر يوم"), gDate(o.lastDay, lang) + L(" (30 days by employee)", " (30 يومًا من الموظف)"), settled ? "done" : ""],
              ].map(([a, b, c], i) => (
                <li key={i} className={cx("step", c)}><span className="sd">{c === "done" ? <Icon name="check" className="s" /> : i + 1}</span><span className="st">{a}</span><span className="sm2">{b}</span></li>
              ))}
            </ol>
          ) : noEosb ? (
            <Alert tone="warn" tint title={L("Ending during probation", "الإنهاء خلال التجربة")} desc={L("No EOSB is due if either party ends the contract during probation (Art. 54).", "لا تستحق مكافأة إذا أنهى أحد الطرفين العقد خلال التجربة (م 54).")} />
          ) : (
            <Kv items={[[L("Submitted", "تاريخ التقديم"), gDate(o.submitted, lang)], [L("Last day", "آخر يوم"), gDate(o.lastDay, lang)]]} />
          )}
          <div className="prs-h" style={{ margin: "8px 0 0" }}>{L("Clearance checklist", "قائمة إخلاء الطرف")}</div>
          {settled && <Alert tone="ok" tint testId="offb-settled" title={L("Clearance complete · employee terminated", "اكتمل إخلاء الطرف · تم إنهاء الخدمة")} />}
          {CLR_TASKS.map(([en, ar], i) => (
            <label key={en} className="check">
              <input type="checkbox" checked={!!o.clearance[i]} disabled={settled || toggle.isPending} data-testid={"clr-" + i}
                onChange={() => toggle.mutate({ id: o.id, index: i }, {
                  onSuccess: (r) => { if (r.status === "Settled") toast(L("Clearance complete · " + (e?.nameEn ?? "") + " marked as terminated", "اكتمل إخلاء الطرف")); },
                  onError: (err) => toast(errMsg(err), "bad"),
                })} /> {L(en, ar)}
            </label>
          ))}
        </div>
        <div className="stack" data-testid="settlement">
          <div className="prs-h" style={{ margin: 0 }}>{L("Final settlement (preview)", "المخالصة النهائية (معاينة)")}</div>
          {sq.error ? <ErrorBox error={sq.error} /> : !st ? <Skeleton /> : (
            <Kv items={[
              [L("Salary for " + st.days + " days", "راتب " + st.days + " يومًا"), <Money key="s" value={st.salary} />],
              [L("Unused leave · " + st.leaveDays + " days", "رصيد الإجازة · " + st.leaveDays + " يوم"), <Money key="l" value={st.leavePay} />],
              [L("End-of-service award", "مكافأة نهاية الخدمة"), noEosb ? <span key="e">—</span> : <span key="e" data-testid="settle-eosb"><Money value={st.eosb} />{o.type === "Resignation" && <span className="xs muted"> ({factor})</span>}</span>],
              [L("Air ticket", "تذكرة السفر"), st.ticket ? <Money key="t" value={st.ticket} /> : "—"],
              [L("Loan balance deducted", "خصم رصيد القرض"), st.loan ? <span key="d"><Money value={-st.ded} />{st.capped && <span className="chip warn">{L("capped at 50%", "بحد 50%")}</span>}</span> : "—"],
              [<b key="nl">{t("Net pay")}</b>, <b key="nv" data-testid="settle-net"><Money value={noEosb ? st.net - st.eosb : st.net} /></b>],
            ]} />
          )}
          <Alert tone="info" tint title={L("Pay within 7 days of the last day", "الصرف خلال 7 أيام من آخر يوم")} desc={L("Employer-ended contracts: within 1 week. Employee resignations: within 2 weeks (Art. 88).", "عند الإنهاء من صاحب العمل خلال أسبوع، وعند الاستقالة خلال أسبوعين (م 88).")} />
        </div>
      </div>
    </Modal>
  );
}

function NewOffbModal({ onClose, preset }: { onClose: () => void; preset?: { employeeId: number; type: string } }) {
  const { t, L, name, lang } = useI18n();
  const toast = useToast();
  const today = useToday();
  const emps = useEmpMap();
  const loans = useLoans();
  const create = useCreateOffboarding();
  const [emp, setEmp] = useState(preset ? String(preset.employeeId) : "");
  const [type, setType] = useState(preset?.type ?? "Resignation");
  const [notice, setNotice] = useState<string | null>(null);
  const [srv, setSrv] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<string | null>(null);
  const active = emps.list.filter((x) => x.status !== "Terminated");
  const empId = Number(emp || active[0]?.id || 0);
  const e = emps.map.get(empId);
  const days = OFFB_TYPES.find((x) => x[0] === type)?.[3] ?? 0;
  const noticeDate = notice ?? today;
  const lastDay = /^\d{4}-\d{2}-\d{2}$/.test(noticeDate) ? addDays(noticeDate, days) : "";
  const dateErr = !lastDay ? "valid date required" : lastDay < today ? "cannot be in the past" : undefined;
  const st = e && lastDay ? settlement(e, lastDay, type, (loans.data ?? []).slice().sort((a, b) => a.id - b.id), today, activeCode()) : null;
  const errs: Record<string, string | undefined> = { lastDay: dateErr, ...srv };

  const submit = async () => {
    if (dateErr || !e) return;
    try {
      await create.mutateAsync({ employeeId: empId, type, lastDay });
      toast(L("Offboarding started for " + e.nameEn, "بدأ إنهاء الخدمة لـ " + e.nameAr));
      onClose();
    } catch (err) {
      const fe = (err as { fields?: Record<string, string> }).fields;
      if (fe && Object.keys(fe).length) setSrv(fe);
      else if ((err as { status?: number }).status === 409) setConflict(errMsg(err));
      else toast(errMsg(err), "bad");
    }
  };
  return (
    <Modal className="wide" title={L("Start offboarding", "بدء إنهاء الخدمة")} onClose={onClose} testId="offb-new"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={submit} loading={create.isPending} disabled={!!dateErr} data-testid="offb-submit">{L("Start offboarding", "بدء إنهاء الخدمة")}</Button></>}>
      <div className="g g-2">
        <div className="stack">
          <SelectField label={t("Employee")} value={String(empId || "")} onChange={(v) => { setEmp(v); setConflict(null); setSrv({}); }} options={active.map((x) => [x.id, name(x) + " · " + x.code])} err={errs.employeeId} testId="offb-emp" />
          <SelectField label={t("Reason")} value={type} onChange={(v) => { setType(v); setSrv({}); }} options={OFFB_TYPES.map(([k, en, ar]): [string, string] => [k, L(en, ar)])} err={errs.type} testId="offb-type" />
          <TextField label={L("Notice given on", "تاريخ الإشعار")} type="date" value={noticeDate} onChange={(v) => { setNotice(v); setSrv({}); }} testId="offb-date" />
          <Alert tone={errs.lastDay ? "bad" : "info"} tint testId="offb-lastday" title={L("Last working day: ", "آخر يوم عمل: ") + (lastDay ? gDate(lastDay, lang) : "—")}
            desc={errs.lastDay ? errs.lastDay : days ? L(days + "-day notice (2025 amendments). You can pay in lieu of notice instead.", "إشعار " + days + " يومًا. يمكن الدفع بدلًا من الإشعار.") : L("No notice period for this reason.", "لا توجد فترة إشعار لهذا السبب.")} />
          {conflict && <Alert tone="bad" testId="offb-conflict" title={L("Cannot start offboarding", "تعذر بدء إنهاء الخدمة")} desc={conflict} />}
        </div>
        <div className="stack" data-testid="offb-estimate">
          <div className="prs-h" style={{ margin: 0 }}>{L("Estimated settlement", "المخالصة التقديرية")}</div>
          {st && e ? (
            <Kv items={[
              [L("EOSB", "المكافأة"), type === "Probation not confirmed" ? "—" : <Money key="e" value={st.eosb} />],
              [L("Leave encashment", "بدل الإجازة"), <Money key="l" value={st.leavePay} />],
              [L("Ticket", "التذكرة"), st.ticket ? <Money key="t" value={st.ticket} /> : "—"],
              [L("Compensation (Art. 77)", "التعويض (م 77)"), type === "Termination" ? <Money key="c" value={Math.max(2 * wageOf(e), ((15 * wageOf(e)) / 30) * st.eo.years)} /> : "—"],
            ]} />
          ) : <Skeleton />}
        </div>
      </div>
    </Modal>
  );
}

type Tab = "onboarding" | "probation" | "offboarding";

export default function Lifecycle() {
  const { L, t, lang, isAr } = useI18n();
  const toast = useToast();
  const today = useToday();
  const onb = useOnboarding();
  const offb = useOffboarding();
  const emps = useEmpMap();
  const [tab, setTab] = useState<Tab>("offboarding");
  const [modal, setModal] = useState<null | { k: "onb"; id: number } | { k: "offb"; id: number } | { k: "new"; preset?: { employeeId: number; type: string } }>(null);

  const err = onb.error ?? offb.error;
  if (err) return <ErrorBox error={err} onRetry={() => { void onb.refetch(); void offb.refetch(); }} />;
  if (!onb.data || !offb.data) return <Skeleton />;
  const probation = emps.list.filter((e) => e.probationEnd && e.status !== "Terminated");
  const person = (id: number): Pick<Employee, "nameEn" | "nameAr" | "code" | "title"> => emps.map.get(id) ?? unknownEmp(id);

  let body: React.ReactNode;
  if (tab === "onboarding") {
    body = onb.data.length === 0 ? <EmptyState title={L("No one is onboarding", "لا يوجد موظفون قيد المباشرة")} /> : (
      <div className="tw">
        <table className="tbl cards" data-testid="onb-table">
          <thead><tr><th>{t("Employee")}</th><th>{L("Start date", "تاريخ المباشرة")}</th><th>{L("Progress", "التقدم")}</th><th className="hide-md">{L("Next step", "الخطوة التالية")}</th></tr></thead>
          <tbody>
            {onb.data.map((o) => {
              const done = o.tasks.filter(Boolean).length, nx = o.tasks.indexOf(0);
              return (
                <tr key={o.employeeId} className="click" tabIndex={0} data-testid={"onb-row-" + o.employeeId} onClick={() => setModal({ k: "onb", id: o.employeeId })} onKeyDown={(ev) => { if (ev.key === "Enter") setModal({ k: "onb", id: o.employeeId }); }}>
                  <td className="k1"><Person e={person(o.employeeId)} /></td>
                  <td className="x" data-l="">{gDate(o.start, lang)}</td>
                  <td className="k3" data-l={L("Progress", "التقدم")} style={{ minWidth: 160 }}><div className="row nw"><div style={{ flex: 1 }}><Bar value={done} max={ONB_TASKS.length} /></div><span className="num xs">{done}/{ONB_TASKS.length}</span></div></td>
                  <td className="hide-md">{nx >= 0 && nx < ONB_TASKS.length ? L(ONB_TASKS[nx][0], ONB_TASKS[nx][1]) : <StatusChip status="Completed" />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  } else if (tab === "probation") {
    body = (
      <>
        <div className="card-b"><Alert tone="info" tint title={L("Probation can be up to 180 days if written in the contract (2025 amendments)", "يمكن أن تصل فترة التجربة إلى 180 يومًا إذا نص عليها العقد (تعديلات 2025)")} desc={L("Sick leave days pause the probation clock. Confirm, extend or end before the date passes.", "أيام الإجازة المرضية توقف احتساب فترة التجربة. أكّد أو مدّد أو أنهِ قبل انتهائها.")} /></div>
        {probation.length === 0 ? <EmptyState title={L("No employees on probation", "لا يوجد موظفون تحت التجربة")} /> : (
          <div className="tw">
            <table className="tbl cards" data-testid="prob-table">
              <thead><tr><th>{t("Employee")}</th><th>{t("Joined")}</th><th>{L("Probation ends", "نهاية التجربة")}</th><th className="end">{t("Actions")}</th></tr></thead>
              <tbody>
                {probation.map((e) => {
                  const dl = daysTo(today, e.probationEnd!);
                  return (
                    <tr key={e.id} data-testid={"prob-row-" + e.id}>
                      <td className="k1"><Person e={e} /></td>
                      <td className="x" data-l="">{gDate(e.joined, lang)}</td>
                      <td className="k2">{dl <= 30 ? <span className="chip orange dot">{L(dl + " days", dl + " يوم")}</span> : <span className="chip info dot">{gDate(e.probationEnd, lang)}</span>}</td>
                      <td className="end k3" data-l="">
                        <div className="row nw" style={{ justifyContent: "flex-end" }}>
                          <Button variant="ghost sm" onClick={() => setModal({ k: "new", preset: { employeeId: e.id, type: "Probation not confirmed" } })} data-testid={"btn-prob-end-" + e.id}>{L("End", "إنهاء")}</Button>
                          <Button variant="ghost sm" onClick={() => toast(L("Probation extended to 180 days", "تم التمديد إلى 180 يومًا"))} data-testid={"btn-prob-extend-" + e.id}>{L("Extend", "تمديد")}</Button>
                          <Button variant="sm" onClick={() => toast(L(e.nameEn + " confirmed — letter generated", "تم التثبيت وإصدار الخطاب"))} data-testid={"btn-prob-confirm-" + e.id}>{L("Confirm", "تثبيت")}</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  } else {
    body = offb.data.length === 0 ? <EmptyState title={L("No offboarding in progress", "لا توجد حالات إنهاء خدمة")} /> : (
      <div className="tw">
        <table className="tbl cards" data-testid="offb-table">
          <thead><tr><th>{t("Employee")}</th><th>{t("Type")}</th><th className="hide-md">{L("Submitted", "تاريخ التقديم")}</th><th>{L("Last day", "آخر يوم")}</th><th>{L("Clearance", "إخلاء الطرف")}</th><th>{t("Status")}</th></tr></thead>
          <tbody>
            {offb.data.map((o: Offboarding) => {
              const done = o.clearance.filter(Boolean).length;
              return (
                <tr key={o.id} className="click" tabIndex={0} data-testid={"offb-row-" + o.id} onClick={() => setModal({ k: "offb", id: o.id })} onKeyDown={(ev) => { if (ev.key === "Enter") setModal({ k: "offb", id: o.id }); }}>
                  <td className="k1"><Person e={person(o.employeeId)} /></td>
                  <td className="x" data-l={t("Type")}>{typeLabel(o.type, isAr)}{o.acknowledged && <span className="xs muted"> · {L("acknowledged", "تم الاستلام")}</span>}</td>
                  <td className="hide-md">{gDate(o.submitted, lang)}</td>
                  <td className="k3" data-l={L("Last day", "آخر يوم")}>{gDate(o.lastDay, lang)} <span className="xs muted">({daysTo(today, o.lastDay)}{L("d", " يوم")})</span></td>
                  <td className="x" data-l="" style={{ minWidth: 120 }}><Bar value={done} max={6} /><span className="xs muted num">{done}/6</span></td>
                  <td className="k2"><StatusChip status={o.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <PageHeader title={L("Onboarding & offboarding", "المباشرة وإنهاء الخدمة")} sub={L("Checklists for joiners, probation reviews, resignations, notice periods and final settlements.", "قوائم للمنضمين ومراجعة التجربة والاستقالات وفترات الإشعار والمخالصات.")}
        actions={<Button variant="pri" icon="log-out" onClick={() => setModal({ k: "new" })} data-testid="btn-new-offb">{L("Start offboarding", "بدء إنهاء الخدمة")}</Button>} />
      <section className="card">
        <Tabs<Tab> label={L("Lifecycle", "دورة حياة الموظف")} value={tab} onChange={setTab} items={[["onboarding", L("Onboarding", "المباشرة"), onb.data.length], ["probation", L("Probation", "فترة التجربة"), probation.length], ["offboarding", L("Offboarding", "إنهاء الخدمة"), offb.data.length]]} />
        {body}
      </section>
      {modal?.k === "onb" && <OnbModal employeeId={modal.id} onClose={() => setModal(null)} />}
      {modal?.k === "offb" && <OffbModal id={modal.id} onClose={() => setModal(null)} />}
      {modal?.k === "new" && <NewOffbModal preset={modal.preset} onClose={() => setModal(null)} />}
    </>
  );
}
